<template>
  <HrPageLayout
    title="Training Dashboard"
    subtitle="Overview and statistics for the Training Management Module"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Training Management Module' },
      { label: 'Training Dashboard' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadDashboard">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="tmd-wrap">
      <div v-if="loadError" class="hr-alert hr-alert-danger hr-mb-3">
        {{ loadError }}
        <button type="button" class="hr-btn hr-btn-sm hr-mt-2" @click="loadDashboard">Retry</button>
      </div>

      <div v-if="loading && !stats.catalogTotal && !loadError" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted">Loading training overview…</p>
      </div>

      <template v-else>
        <!-- KPI metrics -->
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1" @click="go('trainings-catalog')" role="button" tabindex="0">
            <div class="hr-metric-icon"><i class="bx bx-book"></i></div>
            <p class="hr-metric-label">Catalog courses</p>
            <h2 class="hr-metric-value">{{ stats.catalogTotal || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ stats.catalogActive || 0 }} active</p>
          </div>
          <div class="hr-metric-card gradient-2" @click="go('training-calendar')" role="button" tabindex="0">
            <div class="hr-metric-icon"><i class="bx bx-calendar-star"></i></div>
            <p class="hr-metric-label">Upcoming sessions</p>
            <h2 class="hr-metric-value">{{ stats.schedulesUpcoming || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ stats.schedulesTotal || 0 }} total scheduled</p>
          </div>
          <div class="hr-metric-card gradient-4" @click="go('employee-certifications')" role="button" tabindex="0">
            <div class="hr-metric-icon"><i class="bx bx-certification"></i></div>
            <p class="hr-metric-label">Certifications</p>
            <h2 class="hr-metric-value">{{ stats.certificationsTotal || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ stats.certificationsExpiringSoon || 0 }} expiring (90 days)</p>
          </div>
          <div class="hr-metric-card gradient-3" @click="go('training-management')" role="button" tabindex="0">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">Attendance confirms</p>
            <h2 class="hr-metric-value">{{ stats.acknowledgements || 0 }}</h2>
            <p class="hr-small hr-text-muted">{{ stats.reportSubmissions || 0 }} training reports</p>
          </div>
        </div>

        <div class="hr-metrics-grid hr-mb-4 tmd-secondary-metrics">
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
            <p class="hr-metric-label">Expired certs</p>
            <h3 class="hr-metric-value">{{ stats.certificationsExpired || 0 }}</h3>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-history"></i></div>
            <p class="hr-metric-label">Past sessions</p>
            <h3 class="hr-metric-value">{{ stats.schedulesPast || 0 }}</h3>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
            <p class="hr-metric-label">Report submissions</p>
            <h3 class="hr-metric-value">{{ stats.reportSubmissions || 0 }}</h3>
          </div>
          <div class="hr-metric-card gradient-3" @click="go('training-evaluations')" role="button" tabindex="0">
            <div class="hr-metric-icon"><i class="bx bx-clipboard"></i></div>
            <p class="hr-metric-label">Evaluations</p>
            <h3 class="hr-metric-value">—</h3>
            <p class="hr-small hr-text-muted">Open evaluations module</p>
          </div>
        </div>

        <!-- Quick links -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-link-alt"></i> Module shortcuts</h5>
          </div>
          <div class="hr-chart-body">
            <div class="tmd-links">
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="go('training-calendar')">
                <i class="mdi mdi-calendar-star"></i> Training Calendar
              </button>
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="go('employee-certifications')">
                <i class="bx bx-certification"></i> Employee Certifications
              </button>
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="go('training-evaluations')">
                <i class="mdi mdi-file-document-edit-outline"></i> Training Evaluations
              </button>
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="go('training-management')">
                <i class="mdi mdi-school"></i> Training Management
              </button>
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="go('trainings-catalog')">
                <i class="bx bx-book"></i> Training Catalog
              </button>
            </div>
          </div>
        </div>

        <div class="tmd-grid">
          <!-- Upcoming -->
          <div class="hr-chart-card">
            <div class="hr-chart-header hr-flex-between">
              <h5 class="hr-chart-title"><i class="bx bx-calendar-event"></i> Upcoming training sessions</h5>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="go('training-calendar')">Calendar</button>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-hover">
                  <thead>
                    <tr>
                      <th>Training</th>
                      <th>Dates</th>
                      <th>Venue</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="e in upcomingEvents" :key="'up-' + e.schedule_id">
                      <td>
                        <strong>{{ e.trainingName || '—' }}</strong>
                        <div class="hr-text-muted hr-small">{{ e.trainingCode || '' }}</div>
                      </td>
                      <td>
                        {{ e.start_date }}
                        <span v-if="e.end_date && e.end_date !== e.start_date"> – {{ e.end_date }}</span>
                      </td>
                      <td>{{ e.venue || '—' }}</td>
                    </tr>
                    <tr v-if="!upcomingEvents.length">
                      <td colspan="3" class="hr-text-center hr-text-muted hr-py-3">No upcoming sessions scheduled.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Catalog by type -->
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt-2"></i> Catalog by type</h5>
            </div>
            <div class="hr-chart-body">
              <div v-if="!catalogByType.length" class="hr-text-muted hr-text-center hr-py-3">No catalog data yet.</div>
              <ul v-else class="tmd-type-list">
                <li v-for="row in catalogByType" :key="row.type">
                  <span class="tmd-type-name">{{ row.type }}</span>
                  <span class="hr-badge hr-badge-primary">{{ row.count }}</span>
                  <div class="tmd-bar-track">
                    <div class="tmd-bar-fill" :style="{ width: typeBarWidth(row.count) }"></div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="tmd-grid hr-mt-4">
          <!-- Recent schedules -->
          <div class="hr-chart-card">
            <div class="hr-chart-header hr-flex-between">
              <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Recent schedules</h5>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="go('training-management')">Manage</button>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-hover">
                  <thead>
                    <tr>
                      <th>Training</th>
                      <th>Dates</th>
                      <th>Confirmed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in recentSchedules" :key="'rs-' + s.schedule_id">
                      <td>
                        <strong>{{ s.trainingName || '—' }}</strong>
                        <div class="hr-text-muted hr-small">{{ s.trainingType || s.trainingCode || '' }}</div>
                      </td>
                      <td>
                        {{ s.start_date }}
                        <span v-if="s.end_date && s.end_date !== s.start_date"> – {{ s.end_date }}</span>
                      </td>
                      <td><span class="hr-badge hr-badge-success">{{ s.confirmed_count || 0 }}</span></td>
                    </tr>
                    <tr v-if="!recentSchedules.length">
                      <td colspan="3" class="hr-text-center hr-text-muted hr-py-3">No schedules yet.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Recent certifications -->
          <div class="hr-chart-card">
            <div class="hr-chart-header hr-flex-between">
              <h5 class="hr-chart-title"><i class="bx bx-badge-check"></i> Recent certifications</h5>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="go('employee-certifications')">View all</button>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-hover">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Certificate</th>
                      <th>Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in recentCertifications" :key="'rc-' + c.certificateID">
                      <td>{{ c.empNo || '—' }}</td>
                      <td>
                        <strong>{{ c.certificateName || c.trainingName || '—' }}</strong>
                        <div class="hr-text-muted hr-small">{{ c.issuingBody || '' }}</div>
                      </td>
                      <td>
                        <span :class="expiryClass(c.expiryDate)">{{ c.expiryDate || '—' }}</span>
                      </td>
                    </tr>
                    <tr v-if="!recentCertifications.length">
                      <td colspan="3" class="hr-text-center hr-text-muted hr-py-3">No certifications recorded.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <p v-if="generatedAt" class="hr-text-muted hr-small hr-mt-3">Last updated: {{ generatedAt }}</p>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "TrainingRoleDashboard",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      loadError: "",
      stats: {},
      catalogByType: [],
      recentSchedules: [],
      upcomingEvents: [],
      recentCertifications: [],
      generatedAt: ""
    };
  },
  mounted() {
    this.loadDashboard();
  },
  methods: {
    go(name) {
      if (this.$router) this.$router.push({ name });
    },
    typeBarWidth(count) {
      const max = Math.max(1, ...this.catalogByType.map((r) => Number(r.count || 0)));
      const pct = Math.round((Number(count || 0) / max) * 100);
      return Math.max(6, pct) + "%";
    },
    expiryClass(dateStr) {
      if (!dateStr || dateStr === "0000-00-00") return "hr-text-muted";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return "";
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (d < now) return "text-danger";
      const soon = new Date(now);
      soon.setDate(soon.getDate() + 90);
      if (d <= soon) return "text-warning";
      return "";
    },
    async loadDashboard() {
      this.loading = true;
      this.loadError = "";
      try {
        const fd = new FormData();
        fd.append("function", "getTrainingModuleDashboard");
        const res = await execute(fd, authUrl);
        const body = res?.data || {};
        if (body.success === false) {
          this.loadError = body.message || "Failed to load training dashboard.";
          return;
        }
        const payload = body.data || {};
        this.stats = payload.stats || {};
        this.catalogByType = payload.catalogByType || [];
        this.recentSchedules = payload.recentSchedules || [];
        this.upcomingEvents = payload.upcomingEvents || [];
        this.recentCertifications = payload.recentCertifications || [];
        this.generatedAt = payload.generatedAt || "";
      } catch (e) {
        this.loadError = e?.message || "Failed to load training dashboard.";
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.tmd-wrap { padding: 0; }
.tmd-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}
.tmd-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}
@media (max-width: 992px) {
  .tmd-grid { grid-template-columns: 1fr; }
}
.tmd-type-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.tmd-type-list li {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.35rem 0.75rem;
  align-items: center;
  margin-bottom: 0.85rem;
}
.tmd-type-name {
  font-weight: 600;
  color: #334155;
  text-transform: capitalize;
}
.tmd-bar-track {
  grid-column: 1 / -1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}
.tmd-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #0f766e, #0891b2);
  border-radius: 999px;
}
.hr-metric-card[role="button"] {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.hr-metric-card[role="button"]:hover {
  transform: translateY(-2px);
}
.text-danger { color: #dc2626; font-weight: 600; }
.text-warning { color: #d97706; font-weight: 600; }
</style>
